import React, { FC, useEffect, useState } from "react";

import HttpStatus from "http-status-codes";
import log from "loglevel";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { LoaderWithText } from "@/components/atoms";
import config from "@/config";
import { userApi } from "@/shared/apis";
import { UserState } from "@/shared/domain";
import { useAuth, useNavigation, useRouterHook, useToast } from "@/shared/hooks";
import * as tagManager from "@/tagManager";

import ChooseBroker from "../../ChooseBroker/ChooseBroker";

const itsmeContextDefault = {
    itsmeId: null,
    itsmeToken: null,
    email: null,
    isLoading: true,
};

// URL examples
// https://mybroker/itsmeregister/1234567890 >> itsmeSignupComplete >> auto login

const ItsMeRegister: FC = () => {
    const {
        globalState: { lang },
    } = useRouterHook();
    const { navToRoot } = useNavigation();
    const { token } = useParams<any>();
    const { t, i18n } = useTranslation();
    const { prepare, authItsme } = useAuth();
    const { warningToast } = useToast();
    const [itsmeContext, setItsmeContext] = useState(itsmeContextDefault);

    const handleItsmeAuthentication = async (itsmeUserId: string, itsmeToken: string, email: string) => {
        try {
            await authItsme(itsmeUserId, itsmeToken);
            log.info("fAuthItsme success");

            setItsmeContext({
                itsmeId: itsmeUserId,
                itsmeToken,
                email,
                isLoading: false,
            });
        } catch (e) {
            log.warn("ItsmeAuthentication failed", e.message);
            navToRoot();
            if (config.NODE_ENV !== "test") {
                throw e;
            }
        }
    };

    const handleToken = async () => {
        try {
            // Token is received from the email url
            if (token) {
                log.info("itsmeSignupComplete", { token });
                const signupCompleteRes = await userApi.itsmeSignupComplete({ params: { token } });
                log.info("itsmeSignupComplete success");

                log.info("user status", { email: signupCompleteRes.email });
                const resStatus: MyBrokerApi.UserStatus = await userApi.getUserStatus(signupCompleteRes.email);
                log.info("user status success");

                prepare(resStatus.state as UserState);
                await handleItsmeAuthentication(
                    signupCompleteRes.itsMeId,
                    signupCompleteRes.authChallengeToken,
                    signupCompleteRes.email,
                );
                return;
            }

            // If none of the above cases fit, redirect the user to the login page, he is not supposed to see this page
            navToRoot();
        } catch (e) {
            log.warn("itsmeSignupComplete failed", e.message);

            if (e.status === HttpStatus.GONE) {
                warningToast({ message: t("ItsMe.Register.TokenExpired") });
                navToRoot();

                return;
            }
            navToRoot();
        }
    };

    useEffect(() => {
        tagManager.pushLoginEvent("itsme");
        // Switch to language comming from email
        if (lang) i18n.changeLanguage(lang);

        // Handle token from itsme
        handleToken();
    }, []);

    return (
        <>
            {itsmeContext.isLoading ? (
                <LoaderWithText text={t("ItsMe.LoadingMessage")} />
            ) : (
                <ChooseBroker email={itsmeContext.email} fromItsMe />
            )}
            ;
        </>
    );
};

export default ItsMeRegister;
